import '@/components/domain/settings/QandA/QuestionAndAnswerBadge.css'
import '@/styles/globals.css'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import { AppContext } from 'next/app'
import Head from 'next/head'
import React from 'react'
import { RecoilRoot } from 'recoil'
import { SWRConfig } from 'swr'

import { Toast } from '@/components/common/Feedback'
import { useHeaderTokenMutators } from '@/globalStates'

import '../utils/firebase' // Initialize FirebaseApp
import { changeLocale } from '@/i18n/configs'

type HeaderToken = {
  jwt: string
  deviceId: string
  fcmTokenId: string
  firebaseUserId: string
  version: string
  languageCode: string
}

export type NextPageWithLayout = NextPage & {
  getLayout?: (page: React.ReactElement) => React.ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const App = ({ token }: { token: HeaderToken }) => {
  const { setHeaderToken } = useHeaderTokenMutators()

  React.useEffect(() => {
    if (token.deviceId && token.jwt) {
      setHeaderToken(token)
    }
  }, [setHeaderToken, token])

  return null
}

export default function MyApp(props: AppPropsWithLayout) {
  const Component = props.Component
  const pageProps = props.pageProps as Record<string, unknown>
  const getLayout = Component.getLayout ?? (page => page)

  const headerToken = {
    jwt: pageProps?.jwt,
    deviceId: pageProps?.deviceid,
    fcmTokenId: pageProps?.fcmtokenid,
    firebaseUserId: pageProps?.firebaseuserid,
    version: pageProps?.version,
    languageCode: pageProps?.languagecode,
  } as HeaderToken

  changeLocale(props.router.locale ?? 'ja')

  return getLayout(
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no"
        />
      </Head>
      <Toast />
      <SWRConfig
        value={{
          revalidateOnFocus: false,
          errorRetryCount: 3,
        }}
      >
        <RecoilRoot>
          <App token={headerToken} />
          <Component {...pageProps} />
        </RecoilRoot>
      </SWRConfig>
    </>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const { Component, ctx, router } = appContext
  const { asPath } = router

  // eslint-disable-next-line no-console
  console.log(`[Request Path] ${asPath}`)

  // ディスプレイ名がある場合
  if (Component.displayName) {
    // eslint-disable-next-line no-console
    console.log(`[Component] ${Component.displayName}`)
  } else {
    // eslint-disable-next-line no-console
    console.warn('[Component] no display name')
  }

  // 初期画面プロパティの取得
  const pageProps = Component.getInitialProps
    ? await Component.getInitialProps(ctx)
    : {}

  return {
    pageProps,
  }
}
